import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { SongSection } from './SongSection';
import { VerticalSongStructure } from './VerticalSongStructure';
import { Soundworld } from '../../contexts/SoundworldsContext';

interface Section {
    id: string;
    title: string;
}

interface SectionData {
    name: string;
    chords: string;
    elements: string[];
    bars: number;
}

interface SongSectionsManagerProps {
    soundworld?: Soundworld;
    initialFormData?: Array<{
        name: string;
        bars: number;
        chords?: string;
        elements: string[];
    }>;
    onFormDataChange: (formData: {
        soundworlds: string[];
        tempo: string;
        chords: string;
        form: Array<{
            name: string;
            bars: number;
            chords?: string;
            elements: string[];
        }>;
    }) => void;
    viewMode?: 'horizontal' | 'vertical';
    onViewModeChange?: (mode: 'horizontal' | 'vertical') => void;
}

export const SongSectionsManager: React.FC<SongSectionsManagerProps> = ({ soundworld, initialFormData, onFormDataChange, viewMode: externalViewMode, onViewModeChange }) => {
    const [internalViewMode, setInternalViewMode] = useState<'horizontal' | 'vertical'>('vertical');
    const viewMode = externalViewMode ?? internalViewMode;
    const setViewMode = onViewModeChange ?? setInternalViewMode;
    const [formData, setFormData] = useState<Array<{
        name: string;
        bars: number;
        chords?: string;
        elements: string[];
    }>>([]);
    const [sections, setSections] = useState<Section[]>([
        { id: '1', title: 'Section 1' }
    ]);
    const [sectionData, setSectionData] = useState<Record<string, SectionData>>({});
    const [draggedId, setDraggedId] = useState<string | null>(null);
    const [dragOverId, setDragOverId] = useState<string | null>(null);
    const dragRef = useRef<HTMLDivElement>(null);
    const isUpdatingFromHorizontal = useRef(false);
    const isUpdatingFromVertical = useRef(false);
    const [visibleElements, setVisibleElements] = useState<Set<string>>(new Set());

    const sendFormData = useCallback((data: Array<{
        name: string;
        bars: number;
        chords?: string;
        elements: string[];
    }>) => {
        onFormDataChange({
            soundworlds: soundworld ? [soundworld.name] : [],
            tempo: '120',
            chords: '',
            form: data
        });
    }, [soundworld, onFormDataChange]);

    const syncFormDataToHorizontal = useCallback((data: Array<{
        name: string;
        bars: number;
        chords?: string;
        elements: string[];
    }>) => {
        const newSections: Section[] = [];
        const newSectionData: Record<string, SectionData> = {};

        data.forEach((item, index) => {
            const id = (index + 1).toString();
            newSections.push({
                id,
                title: item.name || `Section ${id}`
            });

            newSectionData[id] = {
                name: item.name || `Section ${id}`,
                chords: item.chords || '',
                elements: item.elements || [],
                bars: item.bars || 8
            };
        });

        setSections(newSections);
        setSectionData(newSectionData);
    }, []);

    useEffect(() => {
        if (initialFormData && initialFormData.length > 0) {
            setFormData(initialFormData);
            syncFormDataToHorizontal(initialFormData);
        } else if (initialFormData === undefined || initialFormData.length === 0) {
            const defaultData = [{ name: 'Section 1', bars: 8, chords: '', elements: [] }];
            setFormData(defaultData);
            syncFormDataToHorizontal(defaultData);
        }
    }, [initialFormData, syncFormDataToHorizontal]);

    const prevViewMode = useRef(viewMode);
    const formDataSnapshotRef = useRef(formData);
    
    useEffect(() => {
        formDataSnapshotRef.current = formData;
    }, [formData]);
    
    useEffect(() => {
        if (viewMode === 'horizontal' && prevViewMode.current !== viewMode) {
            syncFormDataToHorizontal(formDataSnapshotRef.current);
        }
        prevViewMode.current = viewMode;
    }, [viewMode, syncFormDataToHorizontal]);

    useEffect(() => {
        if (viewMode === 'horizontal' && !isUpdatingFromVertical.current) {
            const newFormData = sections.map(section => {
                const data = sectionData[section.id] || { name: section.title, chords: '', elements: [], bars: 8 };
                return {
                    name: data.name,
                    bars: data.bars,
                    chords: data.chords || undefined,
                    elements: data.elements
                };
            });
            
            const formDataString = JSON.stringify(newFormData);
            const currentFormDataString = JSON.stringify(formData);
            
            if (formDataString !== currentFormDataString) {
                isUpdatingFromHorizontal.current = true;
                setFormData(newFormData);
                sendFormData(newFormData);
                setTimeout(() => {
                    isUpdatingFromHorizontal.current = false;
                }, 0);
            }
        }
    }, [sections, sectionData, viewMode, sendFormData]);


    const addSection = () => {
        const newId = (sections.length + 1).toString();
        const newSection = {
            id: newId,
            title: `Section ${newId}`
        };
        setSections([...sections, newSection]);
    };

    const duplicateSection = (id: string) => {
        const section = sections.find(s => s.id === id);
        if (!section) return;

        const newId = (sections.length + 1).toString();
        const newSection = {
            id: newId,
            title: `${section.title} (Copy)`
        };

        const sectionIndex = sections.findIndex(s => s.id === id);
        const newSections = [...sections];
        newSections.splice(sectionIndex + 1, 0, newSection);
        setSections(newSections);

        const originalData = sectionData[id];
        if (originalData) {
            setSectionData(prev => ({
                ...prev,
                [newId]: { ...originalData }
            }));
        }
    };

    const deleteSection = (id: string) => {
        if (sections.length <= 1) return;
        setSections(sections.filter(s => s.id !== id));
        const newSectionData = { ...sectionData };
        delete newSectionData[id];
        setSectionData(newSectionData);
    };

    const updateSectionTitle = (id: string, newTitle: string) => {
        setSections(sections.map(section =>
            section.id === id ? { ...section, title: newTitle } : section
        ));
    };

    const handleSectionDataChange = (id: string, data: SectionData) => {
        setSectionData(prev => ({
            ...prev,
            [id]: data
        }));
    };

    const handleDragStart = (e: React.DragEvent, id: string) => {
        setDraggedId(id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e: React.DragEvent, id: string) => {
        e.preventDefault();
        if (draggedId && draggedId !== id) {
            setDragOverId(id);
        }
    };

    const handleDragLeave = () => {
        setDragOverId(null);
    };

    const handleDrop = (e: React.DragEvent, targetId: string) => {
        e.preventDefault();
        if (!draggedId || draggedId === targetId) return;

        const draggedIndex = sections.findIndex(s => s.id === draggedId);
        const targetIndex = sections.findIndex(s => s.id === targetId);

        const newSections = [...sections];
        const [draggedSection] = newSections.splice(draggedIndex, 1);
        newSections.splice(targetIndex, 0, draggedSection);

        setSections(newSections);
        setDraggedId(null);
        setDragOverId(null);
    };

    const handleDragEnd = () => {
        setDraggedId(null);
        setDragOverId(null);
    };

    const [verticalKey, setVerticalKey] = useState(0);

    const handleViewModeChange = (newMode: 'horizontal' | 'vertical') => {
        if (viewMode === 'horizontal' && newMode === 'vertical') {
            const newFormData = sections.map(section => {
                const data = sectionData[section.id] || { name: section.title, chords: '', elements: [], bars: 8 };
                return {
                    name: data.name,
                    bars: data.bars,
                    chords: data.chords || undefined,
                    elements: data.elements
                };
            });
            
            const newFormDataString = JSON.stringify(newFormData);
            const currentFormDataString = JSON.stringify(formData);
            
            isUpdatingFromHorizontal.current = true;
            
            if (newFormDataString !== currentFormDataString) {
                setFormData(newFormData);
                sendFormData(newFormData);
            }
            
            setVerticalKey(prev => prev + 1);
            setViewMode(newMode);
            
            setTimeout(() => {
                isUpdatingFromHorizontal.current = false;
            }, 100);
        } else {
            setViewMode(newMode);
        }
    };

    return (
        <div className="space-y-4">
            {viewMode === 'horizontal' ? (
                <div className="space-y-4">
                    { sections.map((section) => (
                        <div
                            key={ section.id }
                            ref={ dragRef }
                            draggable
                            onDragStart={ (e) => handleDragStart(e, section.id) }
                            onDragOver={ (e) => handleDragOver(e, section.id) }
                            onDragLeave={ handleDragLeave }
                            onDrop={ (e) => handleDrop(e, section.id) }
                            onDragEnd={ handleDragEnd }
                        >
                            <SongSection
                                id={ section.id }
                                title={ section.title }
                                soundworld={ soundworld }
                                initialChords={ sectionData[section.id]?.chords }
                                initialBars={ sectionData[section.id]?.bars }
                                initialElements={ sectionData[section.id]?.elements }
                                onDuplicate={ duplicateSection }
                                onDelete={ deleteSection }
                                onReorder={ () => { } }
                                onTitleChange={ updateSectionTitle }
                                onSectionDataChange={ handleSectionDataChange }
                                isDragging={ draggedId === section.id }
                                dragOver={ dragOverId === section.id }
                            />
                        </div>
                    )) }

                    <button
                        onClick={ addSection }
                        className="w-full border-2 border-dashed border-border rounded-lg p-6 text-center text-muted-foreground hover:border-primary hover:text-card-foreground transition-colors"
                    >
                        <Plus size={ 20 } className="mx-auto mb-2" />
                        <span className="text-sm font-medium">Add section</span>
                    </button>
                </div>
            ) : (
                <VerticalSongStructure
                    key={`vertical-${verticalKey}`}
                    soundworld={soundworld}
                    initialFormData={formData.length > 0 ? formData : undefined}
                    visibleElements={visibleElements}
                    onVisibleElementsChange={setVisibleElements}
                    onFormDataChange={(newFormData) => {
                        if (isUpdatingFromHorizontal.current) {
                            return;
                        }
                        const form = newFormData.form;
                        const formString = JSON.stringify(form);
                        const currentFormString = JSON.stringify(formData);
                        
                        if (formString !== currentFormString) {
                            isUpdatingFromVertical.current = true;
                            setFormData(form);
                            sendFormData(form);
                            setTimeout(() => {
                                isUpdatingFromVertical.current = false;
                            }, 0);
                        }
                    }}
                />
            )}
        </div>
    );
}; 